import type { PublicEmployee, TireSet } from "@skladiscenje/contracts";

/** Re-exported so components import season display logic from one place, never reimplementing it. */
export { seasonLabel } from "@skladiscenje/core";

/**
 * Renders a wire (ISO string) timestamp the way legacy did: `dd.MM.yyyy
 * HH:mm` (`DateTime.ToString("dd.MM.yyyy HH:mm")`, e.g.
 * AddItemWindow.xaml.cs:143). `null`/unparsable input renders as an empty
 * string rather than throwing — plenty of imported legacy rows have dates
 * that are not dates at all (see `IMPORT-POLICY.md`).
 */
export function formatDateTime(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** `"<name> <lastname>"`, matching how legacy built `receivedBy`/`issuedBy` strings (e.g. AddItemWindow.xaml.cs:342). */
export function formatEmployee(employee: PublicEmployee | null): string {
  return employee ? `${employee.name} ${employee.lastname}` : "";
}

/**
 * One tire set's line for the "combined" list columns (`tyreMakerCombined`,
 * `dimensionCombined`, etc. — WarehouseItem.cs:67-100): each column shows one
 * value per stored set. `field` picks which attribute of the set to render.
 *
 * `separator` defaults to `"\n"` — the table (`ItemTable`/`columns.ts`)
 * stacks a two-set item's values on two lines via `white-space: pre-line`,
 * matching legacy's own `"\r\n"`-joined combined columns exactly. The narrow
 * card list (`ItemCards`) passes `" / "` instead: a card has no per-row
 * table cell to stack lines within, and a bare `\n` collapses to a single
 * space under normal CSS text flow, running two dimensions together
 * illegibly (e.g. "205/55 R16 205/55 R16").
 */
export function combinedTireField(
  tireSets: readonly TireSet[],
  field: keyof Pick<TireSet, "maker" | "dimension" | "usage" | "rimsType" | "quantity">,
  separator = "\n",
): string {
  return [...tireSets]
    .sort((a, b) => a.ordinal - b.ordinal)
    .map((set) => {
      const value = set[field];
      return value === null || value === undefined ? "" : String(value);
    })
    .join(separator);
}
